// frontend/src/utils/session.ts
// Utilities for managing user session and authentication state

export type AppSessionTransport = 'cookie' | 'bearer-tab';

const SESSION_TOKEN_KEY = 'smSessionToken';
const FALLBACK_TOKEN_KEY = 'fallbackSession';
const PENDING_LOGIN_TOKEN_KEY = 'pendingLoginToken';
const PENDING_LOGIN_TIME_KEY = 'pendingLoginTime';
const PENDING_LOGIN_MAX_AGE_MS = 30_000;

export const getAppSessionTransport = (
  hostname = window.location.hostname,
): AppSessionTransport => (
  hostname.toLowerCase() === 'app.xprojex.com' ? 'cookie' : 'bearer-tab'
);

export const getAppSessionCredentials = (
  hostname = window.location.hostname,
): RequestCredentials => (
  getAppSessionTransport(hostname) === 'cookie' ? 'include' : 'omit'
);

export const shouldIncludeAppSessionCookies = (
  hostname = window.location.hostname,
): boolean => getAppSessionTransport(hostname) === 'cookie';

export const isLocalAuthHost = (hostname = window.location.hostname): boolean => (
  ['localhost', '127.0.0.1'].includes(hostname)
);

export const navigateToLoginBridge = (url: string): void => {
  window.location.href = url;
};

export function getCurrentAppSessionToken(fallbackToken?: string | null): string {
  const fallback = typeof fallbackToken === 'string' ? fallbackToken.trim() : '';
  if (getAppSessionTransport() === 'cookie') return '';

  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY)?.trim()
      || fallback;
  } catch {
    return fallback;
  }
}

export function readAppSessionToken(): string | null {
  if (getAppSessionTransport() === 'cookie') return null;
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY)?.trim()
      || null;
  } catch {
    return null;
  }
}

const readFreshPendingLoginToken = (): string | null => {
  const token = localStorage.getItem(PENDING_LOGIN_TOKEN_KEY)?.trim() || '';
  const createdAt = Number(localStorage.getItem(PENDING_LOGIN_TIME_KEY));
  const age = Date.now() - createdAt;
  if (token && Number.isFinite(createdAt) && createdAt > 0 && age >= 0 && age < PENDING_LOGIN_MAX_AGE_MS) {
    return token;
  }

  localStorage.removeItem(PENDING_LOGIN_TOKEN_KEY);
  localStorage.removeItem(PENDING_LOGIN_TIME_KEY);
  return null;
};

export function readLegacyAppSessionToken(): string | null {
  try {
    return localStorage.getItem(FALLBACK_TOKEN_KEY)?.trim()
      || readFreshPendingLoginToken()
      || localStorage.getItem(SESSION_TOKEN_KEY)?.trim()
      || null;
  } catch {
    return null;
  }
}

export function storeAppSessionToken(token?: string | null): void {
  const normalized = typeof token === 'string' ? token.trim() : '';
  if (!normalized) return;

  if (getAppSessionTransport() === 'bearer-tab') {
    sessionStorage.setItem(SESSION_TOKEN_KEY, normalized);
  }
  clearLegacyAppSessionTokens();
}

export function clearLegacyAppSessionTokens(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(FALLBACK_TOKEN_KEY);
    localStorage.removeItem(PENDING_LOGIN_TOKEN_KEY);
    localStorage.removeItem(PENDING_LOGIN_TIME_KEY);
  } catch {
    // Cookie authentication can continue when browser storage is unavailable.
  }
}

export function clearAppSessionToken(): void {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // Storage can be unavailable in constrained browser contexts.
  }
  clearLegacyAppSessionTokens();
}
